var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import withSDK from '../../../core/hocs/withSDK';
import Switch from '../../../core/components/Switch';
import Button from '../../../core/components/Button';
import Radios from '../../../core/components/Radio';
import { useAsyncCallback } from '../../../core/hooks/useAsyncCallback';
import useElement from '../../../core/hooks/useElement';
import useUserFilters from '../../../core/hooks/useUserFilters';
import customizableComponent from '../../../core/hocs/customization';
import AvatarUploader from './AvatarUploader';
import { isEqual } from '../../../helpers';
import { notification } from '../../../core/components/Notification';
import CategorySelector from './CategorySelector';
import UserSelector from '../../../social/components/UserSelector';
import { Form, TextField, AboutTextarea, SwitchContainer, Footer, SubmitButton, Description, FormBlockContainer, FormBlockHeader, FormBlockBody, PermissionControlContainer, IconWrapper, LockIcon, WorldIcon, Counter, Label, LabelCounterWrapper, Field, MembersField, ErrorMessage, FormBody, } from './styles';
const FormBlock = ({ title, children, edit }) => (React.createElement(FormBlockContainer, { edit: edit },
    edit && title && React.createElement(FormBlockHeader, null, title),
    React.createElement(FormBlockBody, { edit: edit }, children)));
const CommunityTypeItem = ({ type, description, icon }) => (React.createElement(PermissionControlContainer, null,
    React.createElement(IconWrapper, null, icon),
    React.createElement("div", null,
        type,
        React.createElement(Description, null, description))));
function useKeepScrollBottom(ref, deps) {
    const scrollBottom = ref.current && ref.current.scrollHeight - ref.current.clientHeight - ref.current.scrollTop;
    React.useLayoutEffect(() => {
        if (ref.current && scrollBottom < 10) {
            const scrollBottomAfterRender = ref.current.scrollHeight - ref.current.clientHeight - ref.current.scrollTop;
            if (scrollBottomAfterRender > 10) {
                ref.current.scrollTo({ top: ref.current.scrollHeight });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref.current, scrollBottom, ...deps]);
}
const CommunityForm = ({ 'data-qa-anchor': dataQaAnchor = '', community, // initialize form on editing
edit, onSubmit, className, onCancel, canCreatePublic, currentUserId, }) => {
    const { formatMessage } = useIntl();
    const defaultValues = useMemo(() => {
        var _a, _b;
        return (Object.assign({ avatarFileId: null, description: '', displayName: '', isPublic: false, tags: [], userIds: [], categoryId: (_b = (_a = community === null || community === void 0 ? void 0 : community.categoryIds) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '' }, community));
    }, [community]);
    const { register, handleSubmit, setError, watch, control, formState } = useForm({
        defaultValues,
    });
    const { localeLanguage, businessType } = useUserFilters(currentUserId);
    const { errors } = formState;
    const displayName = watch('displayName', '');
    const description = watch('description', '');
    const categoryId = watch('categoryId', '');
    const userIds = watch('userIds', []);
    const avatarFileId = watch('avatarFileId', null);
    // what the hell...
    // The logic is very overcomplicated, but left like this just to fix a bug until a proper refactor can be done.
    const isPublicFromForm = watch('isPublic', true);
    const isPublic = (typeof isPublicFromForm === 'boolean' && isPublicFromForm) || isPublicFromForm === 'true';
    const [isDirty, markDirty] = useState(false);
    useEffect(() => {
        markDirty(!isEqual(defaultValues, Object.assign(Object.assign({}, defaultValues), { displayName,
            description,
            categoryId,
            userIds,
            isPublic,
            avatarFileId })));
    }, [displayName, description, categoryId, userIds, isPublic, avatarFileId, defaultValues]);
    const [validateAndSubmit, submitting] = useAsyncCallback((data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!data.displayName.trim()) {
            setError('displayName', { message: formatMessage({ id: 'nameRequired' }) });
            return;
        }
        // Cannot update community members with this endpoint.
        if (!edit && !isPublic && ((_a = data.userIds) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            setError('userIds', {
                message: formatMessage({ id: 'AddMemberModal.membersValidationError' }),
            });
            return;
        }
        // TODO: A user can have multiple languages, so at some point we should add UI to allow these
        //  users specifically to choose what language their community is being created in.
        //  For now, this is going to be an under-supported feature and communities will default
        //  to the user's primary language, which is presumed to be the first language in their
        //  locale array.
        const payload = {
            displayName: data.displayName,
            description: ((_b = data.description) === null || _b === void 0 ? void 0 : _b.length) ? data.description : undefined,
            avatarFileId: data.avatarFileId,
            tags: [],
            userIds: data.userIds,
            isPublic,
            metadata: { localeLanguage: localeLanguage[0], businessType },
            // Currently we support only one category per community.
            categoryIds: ((_c = data === null || data === void 0 ? void 0 : data.categoryId) === null || _c === void 0 ? void 0 : _c.length) ? [data.categoryId] : undefined,
        };
        // Cannot update community members with this endpoint.
        if (edit) {
            delete payload.userIds;
        }
        yield onSubmit(payload);
        if (edit) {
            notification.success({ content: React.createElement(FormattedMessage, { id: "community.updateSuccess" }) });
        }
    }), [setError, isPublic, onSubmit, edit]);
    const disabled = !isDirty || displayName.length === 0 || categoryId === '' || submitting;
    const [formBodyRef, formBodyElement] = useElement();
    useKeepScrollBottom(formBodyRef, [formState]);
    const communityTypeItems = [
        {
            key: 'public',
            type: formatMessage({ id: 'public' }),
            description: formatMessage({ id: 'publicDescription' }),
            icon: React.createElement(WorldIcon, null),
            customRenderer: CommunityTypeItem,
            value: true,
            'data-qa-anchor': 'community-form-public-type',
        },
        {
            key: 'private',
            type: formatMessage({ id: 'private' }),
            description: formatMessage({ id: 'privateDescription' }),
            icon: React.createElement(LockIcon, null),
            customRenderer: CommunityTypeItem,
            value: false,
            'data-qa-anchor': 'community-form-private-type',
        },
    ];
    const formattedCommunityTypeItems = canCreatePublic
        ? communityTypeItems
        : communityTypeItems.filter((i) => i.key !== 'public');
    return (React.createElement(Form, { className: className, edit: edit, onSubmit: handleSubmit(validateAndSubmit) },
        React.createElement(FormBody, { ref: formBodyRef },
            React.createElement(FormBlock, { title: "General", edit: edit },
                React.createElement(Field, null,
                    React.createElement(Controller, { name: "avatarFileId", control: control, render: (_a) => {
                            var _b = _a.field, { ref } = _b, rest = __rest(_b, ["ref"]);
                            return (React.createElement(AvatarUploader, Object.assign({ mimeType: "image/png, image/jpeg" }, rest, { "data-qa-anchor": dataQaAnchor })));
                        }, defaultValue: null })),
                React.createElement(Field, { error: errors.displayName },
                    React.createElement(LabelCounterWrapper, null,
                        React.createElement(Label, { htmlFor: "displayName", className: "required" },
                            React.createElement(FormattedMessage, { id: "community.name" })),
                        React.createElement(Counter, null,
                            displayName.length,
                            "/30")),
                    React.createElement(TextField, Object.assign({}, register('displayName', {
                        required: formatMessage({ id: 'nameRequired' }),
                        maxLength: {
                            value: 30,
                            message: formatMessage({ id: 'nameTooLong' }),
                        },
                    }), { "data-qa-anchor": `${dataQaAnchor}-community-name-input`, placeholder: formatMessage({ id: 'createCommunityNamePlaceholder' }) })),
                    React.createElement(ErrorMessage, { errors: errors, name: "displayName" })),
                React.createElement(Field, { error: errors.description },
                    React.createElement(LabelCounterWrapper, null,
                        React.createElement(Label, { htmlFor: "description" },
                            React.createElement(FormattedMessage, { id: "community.about" })),
                        React.createElement(Counter, null,
                            description.length,
                            "/5000")),
                    React.createElement(AboutTextarea, Object.assign({}, register('description', {
                        maxLength: { value: 5000, message: formatMessage({ id: 'descriptionTooLong' }) },
                    }), { "data-qa-anchor": `${dataQaAnchor}-community-description-textarea`, placeholder: formatMessage({ id: 'enterDescriptionPlaceholder' }) })),
                    React.createElement(ErrorMessage, { errors: errors, name: "description" })),
                React.createElement(Field, { error: errors.categoryId },
                    React.createElement(Label, { htmlFor: "categoryId", className: "required" },
                        React.createElement(FormattedMessage, { id: "community.category" })),
                    React.createElement(Controller, { rules: { required: formatMessage({ id: 'categoryRequired' }) }, name: "categoryId", render: (_a) => {
                            var _b = _a.field, { ref } = _b, rest = __rest(_b, ["ref"]);
                            return (React.createElement(CategorySelector, Object.assign({ parentContainer: formBodyElement }, rest, { "data-qa-anchor": `${dataQaAnchor}` })));
                        }, control: control, defaultValue: "" }),
                    React.createElement(ErrorMessage, { errors: errors, name: "category" }))),
            false && (React.createElement(FormBlock, { title: "Post permission", edit: edit },
                React.createElement(SwitchContainer, null,
                    React.createElement("div", null,
                        React.createElement(Label, null,
                            React.createElement(FormattedMessage, { id: "community.onlyadmincanpost" })),
                        React.createElement(Description, null,
                            React.createElement(FormattedMessage, { id: "community.onlyadmins" }),
                            ",")),
                    React.createElement(Controller, { name: "onlyAdminCanPost", render: ({ field: { value, onChange } }) => (React.createElement(Switch, { value: value, onChange: () => onChange(!value) })), control: control, defaultValue: false })))),
            React.createElement(FormBlock, { title: React.createElement(FormattedMessage, { id: "community.categorypermission" }), edit: edit },
                React.createElement(Controller, { name: "isPublic", render: ({ field: { value, onChange } }) => (React.createElement(Radios, { items: formattedCommunityTypeItems, value: value, onChange: () => onChange(!value) })), control: control })),
            !isPublic && !edit && (React.createElement(FormBlock, { title: "Community members", edit: edit },
                React.createElement(MembersField, { error: errors.userIds },
                    React.createElement(Label, { name: "userIds", className: "required" },
                        React.createElement(FormattedMessage, { id: "community.addmembers" })),
                    React.createElement(Controller, { name: "userIds", render: (_a) => {
                            var _b = _a.field, { ref } = _b, rest = __rest(_b, ["ref"]);
                            return (React.createElement(UserSelector, Object.assign({ parentContainer: formBodyElement, "data-qa-anchor": dataQaAnchor }, rest)));
                        }, control: control }),
                    React.createElement(ErrorMessage, { errors: errors, name: "userIds" }))))),
        React.createElement(Footer, { edit: edit },
            !edit && (React.createElement(Button, { onClick: (e) => {
                    e.preventDefault();
                    onCancel();
                } },
                React.createElement(FormattedMessage, { id: "cancel" }))),
            React.createElement(SubmitButton, { "data-qa-anchor": `${dataQaAnchor}-${edit ? 'save' : 'create'}-button`, disabled: disabled, edit: edit }, edit ? React.createElement(FormattedMessage, { id: "save" }) : React.createElement(FormattedMessage, { id: "create" })))));
};
export default memo(withSDK(customizableComponent('CommunityForm', CommunityForm)));
//# sourceMappingURL=index.js.map